import React, { useEffect, useRef } from "react";
import { ScrollView, Sheet, View } from "tamagui";
import SheetHeader from "@screen/Register/components/SheetHeader";
import { IoIosArrowBack } from "react-icons/io";
import { colors } from "@styles/colors";
import { IconType } from "react-icons";
import { zIndexes } from "@utils/zIndexLevel";

/*******************************************************************
 * Type: Sheet 組件
 * Description:
 *     1. 用於設定 tamagui <Sheet> 組件所需的基本設定
 *     2. 使用時直接傳入 Child component 內容
 * Params:
 *     - isOpen: 控制 Sheet 打開
 *     - onOpenChange: 用於更新 isOpen 的函數
 *     - title: Sheet 的標題（可選）
 *     - h: Sheet 的高度，默認為100（可選）
 *     - children: Sheet 內部顯示的內容
 *     - bgColor: Sheet 背景顏色（可選）
 *     - gap: 內容之間的間距 margin（可選）
 *     - headerProps: 標題區域的自定義屬性，包括：
 *         - headerBG: 標題區域背景顏色（可選）
 *         - rightIcon: 右側 icon 及其屬性（可選）
 *         - leftIcon: 左側 icon 及其屬性（可選）
 *         - others: 其他自定義屬性（可選）
 ******************************************************************/

interface SheetWrapperProps {
  isOpen: boolean;
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
  title?: string;
  h?: number;
  children: React.ReactNode;
  bgColor?: string;
  gap?: string;
  headerProps?: {
    headerBG?: string;
    rightIcon?: {
      icon: IconType;
      color?: string;
      bg?: string;
      onPress?: () => void;
    };
    leftIcon?: {
      icon: IconType;
      color?: string;
      bg?: string;
      onPress?: () => void;
    };
    others?: any;
  };
  sheetToTop?: boolean;
  /** 覆寫預設 zIndex，用於需要高於頁面底部固定區塊的 sheet */
  zIndex?: number;
}

const SheetWrapper: React.FC<SheetWrapperProps> = ({
  isOpen,
  onOpenChange,
  title,
  h = 100,
  children,
  bgColor,
  gap,
  headerProps,
  sheetToTop,
  zIndex: zIndexOverride,
}) => {
  const scrollViewRef = useRef<ScrollView | null>(null); // 使用useRef引用ScrollView

  const closeThisSheet = () => {
    onOpenChange(false);
  };

  const scrollToTop = () => {
    scrollViewRef.current?.scrollTo({ y: 0, animated: false });
  };

  // 當isOpen變化時，重置滾動位置
  useEffect(() => {
    if (isOpen && scrollViewRef.current) {
      // 重置ScrollView的位置
      scrollToTop();
    }
  }, [isOpen]);

  // 當isOpen變化時，重置滾動位置
  useEffect(() => {
    if (sheetToTop) {
      // 重置ScrollView的位置
      scrollToTop();
    }
  }, [sheetToTop]);

  return (
    <Sheet
      disableDrag
      open={isOpen}
      onOpenChange={onOpenChange}
      position={0}
      animation="medium"
      snapPoints={[h, 10]}
      modal={true}
      snapPointsMode="percent"
      zIndex={zIndexOverride ?? zIndexes.sheet}
      dismissOnOverlayPress={false}
      dismissOnSnapToBottom={false}>
      <Sheet.Overlay
        animation="lazy"
        enterStyle={{ opacity: 0 }}
        exitStyle={{ opacity: 0 }}
        style={{
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
      />

      <Sheet.Frame
        disableHideBottomOverflow
        bg={bgColor ? bgColor : "#FFF"}
        borderTopRightRadius={h === 100 ? 0 : "$6"}
        borderTopLeftRadius={h === 100 ? 0 : "$6"}
        h={"100%"}
        w={"100%"}>
        <ScrollView ref={scrollViewRef} h={"100%"} w={"100%"}>
          {/* 標題區域 */}
          {title && (
            <View
              minHeight={60}
              bg={colors.orderListBg}
              justifyContent={"center"}
              style={{ position: "sticky" }}
              top={0}
              zIndex={zIndexes.stickyHeader}>
              <div>
                {headerProps ? (
                  <SheetHeader
                    title={title ? title : ""}
                    bgColor={headerProps.headerBG ? headerProps.headerBG : "#FFF"}
                    other={headerProps.others}
                    left={
                      headerProps?.leftIcon
                        ? {
                            icon: headerProps.leftIcon.icon,
                            func: headerProps.leftIcon.onPress || closeThisSheet,
                            bg: headerProps.leftIcon.bg,
                          }
                        : undefined
                    }
                    right={
                      headerProps?.rightIcon
                        ? {
                            icon: headerProps.rightIcon.icon,
                            func: headerProps.rightIcon.onPress || closeThisSheet,
                            bg: headerProps.rightIcon.bg,
                          }
                        : undefined
                    }
                  />
                ) : (
                  title && <SheetHeader title={title} left={{ icon: IoIosArrowBack, func: closeThisSheet }} />
                )}
              </div>
            </View>
          )}

          {/* 動態內容 */}
          <View gap={gap ? gap : "unset"} h={"100%"} w={"100%"}>
            {children}
          </View>
        </ScrollView>
      </Sheet.Frame>
    </Sheet>
  );
};

export default SheetWrapper;
